import React, { useState } from "react";
import { Col, Input, InputGroup, Label, Row } from "reactstrap";
import { parseArrayValue } from "@/utils/parseArrayValue";

type ParseError = ReturnType<typeof parseArrayValue>["errors"][number];

export type ArraySimpleFieldProps = {
  settingVar: { variable: string; value?: unknown };
  onChange: (name: string, value: ReturnType<typeof parseArrayValue>["values"]) => void;
};

/**
 * Array (semicolon-separated) building block (SCRUM-553, VariableField
 * registry). Shows the current value read-only ("Old Value") and parses the
 * "New Value" input on blur through the shared parseArrayValue grammar
 * (SCRUM-550): Object/Float/Int/Boolean/Array, everything else a raw
 * string. Emits the parsed array via `onChange(name, value)`.
 *
 * Unparsable elements are dropped from the saved value (unchanged), but
 * since SCRUM-504 each one is shown inline below the input instead of
 * disappearing silently.
 */
export default function ArraySimpleField({ settingVar, onChange }: ArraySimpleFieldProps) {
  const [parseErrors, setParseErrors] = useState<ParseError[]>([]);
  const formatted =
    Array.isArray(settingVar.value) && settingVar.value.length > 0
      ? settingVar.value.map((sett) => JSON.stringify(sett)).join(";")
      : "empty Array";

  return (
    <>
      <Row>
        <Label
          className="form-control-label"
          htmlFor="example-text-input"
          md="3"
          style={{ color: "#597ade" }}
        >
          Old Value
        </Label>
        <Col md="9">
          <Label className="form-control-label" htmlFor="example-text-input" md="9">
            {formatted}
          </Label>
        </Col>
      </Row>
      <Row>
        <Label
          className="form-control-label"
          htmlFor="example-text-input"
          md="3"
          style={{ color: "#597ade" }}
        >
          New Value
        </Label>
        <Col md="9">
          <InputGroup className="input-group-merge">
            <Input
              placeholder={`insert value for ${settingVar.variable}`}
              type="text"
              name={settingVar.variable}
              onBlur={(e) => {
                const { values, errors } = parseArrayValue(e.target.value);
                setParseErrors(errors);
                onChange(settingVar.variable, values);
              }}
            />
          </InputGroup>
          {parseErrors.map((err, i) => (
            // static list: rebuilt whole on every blur, and a row holds no state of its own
            <div key={i} className="text-danger">
              {`could not parse ${err.element} to ${
                err.kind === "json" ? "object" : "array"
              }..will not be added!`}
            </div>
          ))}
        </Col>
      </Row>
    </>
  );
}
